import { z } from 'zod';
import { AccountingEntryItemSchema } from './AccountingEntryItem/AccountingEntryItemSchema';
import { CURRENCY_OPTIONS } from '@/common/constants';
import { fixAmount } from '@/lib/utils';

const hasSelectedOption = (option?: { id?: string } | null) => Boolean(option?.id?.trim());

const AccountingEntrySchema = z
  .object({
    paymentDate: z.date({
      required_error: 'Seleccione una fecha de cobro',
    }),
    accountingEntries: z
      .array(AccountingEntryItemSchema)
      .min(2, 'Ingrese al menos dos partidas contables'),
    transactionId: z.string().optional(),
    donationId: z.string().optional(),
    files: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    const balance = data.accountingEntries.reduce(
      (acc, entry) => acc + entry.amount * entry.exchangeRateOfficial,
      0,
    );

    if (fixAmount(balance, 2) !== 0) {
      return ctx.addIssue({
        code: 'custom',
      });
    }

    data.accountingEntries.forEach((entry, index) => {
      const hasDonation = hasSelectedOption(entry.donation);
      const hasCostCenter = hasSelectedOption(entry.costCenter);
      const hasCategory = hasSelectedOption(entry.category);
      const isGlobalCostCenter = entry.global === true || entry.costCenter?.global === true;

      if (!isGlobalCostCenter && !hasDonation && !hasCostCenter && !hasCategory) {
        return;
      }

      if (!hasCostCenter) {
        ctx.addIssue({
          path: ['accountingEntries', index, 'costCenter'],
          message: 'Debe seleccionar un Centro de Costos para completar la imputación de la línea',
          code: 'custom',
        });
        return;
      }

      if (!hasCategory) {
        ctx.addIssue({
          path: ['accountingEntries', index, 'category'],
          message: 'Debe seleccionar una Categoría para el centro de costos elegido',
          code: 'custom',
        });
      }

      if (isGlobalCostCenter && hasDonation) {
        ctx.addIssue({
          path: ['accountingEntries', index, 'donation'],
          message: 'La donación no aplica para el centro de costos global',
          code: 'custom',
        });
      }

      if (!isGlobalCostCenter && !hasDonation) {
        ctx.addIssue({
          path: ['accountingEntries', index, 'donation'],
          message: 'Debe seleccionar una Donación para completar la imputación de la línea',
          code: 'custom',
        });
      }

      if (
        !isGlobalCostCenter &&
        hasDonation &&
        entry.currency === 'ARS' &&
        entry.donation?.money?.currency &&
        entry.donation.money.currency !== 'ARS'
      ) {
        const exchangeRateAlt = Number(entry.exchangeRateAlt || 0);
        if (exchangeRateAlt <= 1) {
          ctx.addIssue({
            path: ['accountingEntries', index, 'exchangeRateAlt'],
            message: 'Debe ingresar un tipo de cambio alternativo para imputar ARS a una donación',
            code: 'custom',
          });
        }
      }
    });
  });

type AccountingEntryFormType = z.infer<typeof AccountingEntrySchema>;

const defaultAccountingEntryFormValues: AccountingEntryFormType = {
  paymentDate: new Date(),
  accountingEntries: [
    {
      global: false,
      ledgerAccount: {
        id: '',
        name: '',
      },
      currency: CURRENCY_OPTIONS[0].value,
      amount: 0,
      exchangeRateOfficial: 1,
      exchangeRateAlt: 1,
      details: '',
      donation: null,
      costCenter: null,
      category: null,
    },
    {
      global: false,
      ledgerAccount: {
        id: '',
        name: '',
      },
      currency: CURRENCY_OPTIONS[0].value,
      amount: 0,
      exchangeRateOfficial: 1,
      exchangeRateAlt: 1,
      details: '',
      donation: null,
      costCenter: null,
      category: null,
    },
  ],
  files: [],
};

export type { AccountingEntryFormType };
export { AccountingEntrySchema, defaultAccountingEntryFormValues };
